import React, { useMemo, useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { toast } from 'sonner'
import { ArrowLeft, Stethoscope, Lock, AlertTriangle, Clock, Check, PenLine, ShieldCheck } from 'lucide-react'
import DashboardLayout from '../layouts/DashboardLayout'
import { cn } from '../lib/utils'

// The clinical note (Stage 4). The table and the model shipped on 3 August; there was no screen,
// so no note could ever be written. This is that screen.
//
// It is built around one finding from the KB (03 R-13, 18-insurance-position): dental records fail
// as a defence because they are CLINICALLY INCOMPLETE — essentially never because the technology
// was weak. So the fields are not free-text SOAP boxes; they are the eight specific things a
// practitioner gets pulled up on, with a visible meter showing what is still missing.
//
// The meter DELIBERATELY does not block saving. A blocker makes people type nonsense to clear it,
// which leaves a record that looks complete and says nothing — worse than a gap you can see.

const FIELDS = [
  { key: 'presenting_complaint', label: 'Presenting complaint',
    hint: 'What the patient came in for, ideally in their own words.', rows: 2 },
  { key: 'examination_findings', label: 'Examination findings',
    hint: 'What you saw and measured. The single heaviest item on the record.', rows: 3 },
  { key: 'diagnosis', label: 'Diagnosis', hint: 'What you concluded.', rows: 2 },
  { key: 'benefits_explained', label: 'Benefits explained',
    hint: 'What this treatment is meant to achieve — the “B” of Benefits · Alternatives · Risks · Nothing.', rows: 2 },
  { key: 'options_discussed', label: 'Options discussed',
    hint: 'Every reasonable option put to the patient, not only the one chosen.', rows: 3 },
  { key: 'risks_explained', label: 'Risks explained',
    hint: 'The material risks — the ones this patient would attach significance to (Castell v De Greef).', rows: 3 },
  { key: 'patient_questions', label: 'Patient’s questions and your answers',
    hint: 'What they asked, and what you told them.', rows: 2 },
  { key: 'patient_refused', label: 'Anything the patient declined',
    hint: 'A refusal is part of the record. Leave blank if nothing was declined.', rows: 2 },
  { key: 'plan', label: 'Plan', hint: 'What happens next, and when.', rows: 2 },
]

// Teach-back wording and the one-tap phrases, kept as constants so the prompt a clinician is
// shown is a stable, reviewable string rather than something buried in markup.
const TEACH_BACK_PROMPT =
  '“Just so I know I explained it clearly — can you tell me in your own words what we are doing and why?”'
const QUICK_PHRASES = [
  'Patient explained the plan back accurately.',
  'Patient restated the main risks in their own words.',
  'Patient understood what happens if we do nothing.',
  'Explained again in simpler terms; understanding then confirmed.',
]

const dz = (iso) => (iso ? new Date(iso).toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—')
const blank = () => ({
  presenting_complaint: '', examination_findings: '', diagnosis: '', benefits_explained: '', options_discussed: '',
  risks_explained: '', patient_questions: '', patient_refused: '', plan: '',
  do_nothing_discussed: false, comprehension_checked: false, comprehension_note: '', course_of_treatment_id: '', consent_id: '', consent_record_type: '',
  consent_reconfirmed: false, consent_reconfirmed_note: '', language: 'en', interpreter_name: '',
})

function Meter({ score, missing, weights }) {
  const tone = score >= 85 ? 'bg-emerald-500' : score >= 60 ? 'bg-amber-500' : 'bg-red-500'
  return (
    <div className="rounded-xl border border-brand-border bg-white p-4">
      <div className="mb-2 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-brand-ink">Completeness</h3>
        <span className="text-2xl font-bold text-brand-ink">{score}%</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-brand-border">
        <div className={cn('h-2 rounded-full transition-all', tone)} style={{ width: `${score}%` }} />
      </div>
      {missing.length === 0 ? (
        <p className="mt-3 flex items-center gap-1.5 text-xs text-emerald-700">
          <Check size={13} /> Everything a record is usually challenged on is here.
        </p>
      ) : (
        <>
          <p className="mt-3 text-xs font-medium text-brand-muted">Still missing:</p>
          <ul className="mt-1 space-y-1">
            {missing.map((m) => (
              <li key={m} className="flex items-center justify-between text-xs text-brand-ink">
                <span>{FIELDS.find((f) => f.key === m)?.label || ({ do_nothing_discussed: 'Option of no treatment', comprehension_checked: 'Understanding checked' }[m] || m)}</span>
                <span className="font-mono text-brand-muted">{weights[m]}%</span>
              </li>
            ))}
          </ul>
        </>
      )}
      <p className="mt-3 border-t border-brand-border pt-2 text-[11px] leading-relaxed text-brand-muted">
        This never stops you saving. It is here so a gap is visible now rather than in three years.
      </p>
    </div>
  )
}

function NoteCard({ note, onSign, onAmend }) {
  return (
    <div className={cn('rounded-xl border bg-white p-4',
      note.superseded ? 'border-brand-border/60 opacity-70' : 'border-brand-border')}>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-brand-ink">{dz(note.encounter_at)}</span>
        {note.locked ? (
          <span className="inline-flex items-center gap-1 rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700">
            <Lock size={10} /> Signed by {note.signed_by} · {note.signed_at}
          </span>
        ) : (
          <span className="rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700">Draft — not signed</span>
        )}
        {note.late_entry && (
          <span className="inline-flex items-center gap-1 rounded border border-orange-200 bg-orange-50 px-1.5 py-0.5 text-[11px] font-semibold text-orange-700">
            <Clock size={10} /> Late entry
          </span>
        )}
        {note.superseded && (
          <span className="rounded border border-brand-border bg-brand-surface px-1.5 py-0.5 text-[11px] font-semibold text-brand-muted">
            Superseded — kept on the record
          </span>
        )}
        {note.supersedes_id && (
          <span className="rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[11px] font-semibold text-blue-700">
            Amends note #{note.supersedes_id}
          </span>
        )}
        <span className="ml-auto text-xs font-semibold text-brand-muted">{note.completeness}% complete</span>
      </div>

      {note.late_entry && note.late_entry_reason && (
        <p className="mb-2 text-xs italic text-orange-800">Reason: {note.late_entry_reason}</p>
      )}

      <dl className="space-y-1.5">
        {FIELDS.map((f) => note[f.key] && (
          <div key={f.key} className="grid grid-cols-[190px_1fr] gap-2 text-sm">
            <dt className="text-xs font-semibold text-brand-muted">{f.label}</dt>
            <dd className="whitespace-pre-wrap text-brand-ink">{note[f.key]}</dd>
          </div>
        ))}
        <div className="grid grid-cols-[190px_1fr] gap-2 text-sm">
          <dt className="text-xs font-semibold text-brand-muted">Option of no treatment</dt>
          <dd className="text-brand-ink">{note.do_nothing_discussed ? 'Discussed' : '— not recorded —'}</dd>
        </div>
        {(note.language && note.language !== 'en') || note.interpreter_name ? (
          <div className="grid grid-cols-[190px_1fr] gap-2 text-sm">
            <dt className="text-xs font-semibold text-brand-muted">Language</dt>
            <dd className="text-brand-ink">
              {{ af: 'Afrikaans', other: 'Other' }[note.language] || 'English'}
              {note.interpreter_name ? ` · interpreted by ${note.interpreter_name}` : ''}
            </dd>
          </div>
        ) : null}
        <div className="grid grid-cols-[190px_1fr] gap-2 text-sm">
          <dt className="text-xs font-semibold text-brand-muted">Understanding checked</dt>
          <dd className={note.comprehension_checked ? 'text-brand-ink' : 'text-brand-muted'}>
            {note.comprehension_checked
              ? (note.comprehension_note || 'Teach-back completed')
              : '— not recorded —'}
          </dd>
        </div>
      </dl>

      <div className="mt-3 flex gap-2 border-t border-brand-border pt-3">
        {!note.locked && (
          <button onClick={() => onSign(note)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white hover:brightness-95">
            <PenLine size={12} /> Sign &amp; lock
          </button>
        )}
        {note.locked && !note.superseded && (
          <button onClick={() => onAmend(note)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-brand-border px-3 py-1.5 text-xs font-semibold text-brand-ink hover:bg-brand-surface">
            Amend
          </button>
        )}
      </div>
    </div>
  )
}

export default function ClinicalNotes({ patient = {}, notes = [], consents = [], courses = [], weights = {}, stale_after_days = 90 }) {
  const [v, setV] = useState(blank)
  const [saving, setSaving] = useState(false)
  const [amending, setAmending] = useState(null)   // the note being amended
  const [reason, setReason] = useState('')
  const [encounterAt, setEncounterAt] = useState('')

  const set = (k, val) => setV((p) => ({ ...p, [k]: val }))

  // Mirrors ClinicalNote::COMPLETENESS_WEIGHTS exactly — the server recomputes it on save, so
  // this is a live preview, never the stored number.
  const { score, missing } = useMemo(() => {
    let s = 0
    const gone = []
    Object.entries(weights).forEach(([k, w]) => {
      const val = v[k]
      const present = typeof val === 'boolean' ? val : String(val || '').trim().length > 0
      if (present) s += w; else gone.push(k)
    })
    return { score: s, missing: gone }
  }, [v, weights])

  const chosenConsent = consents.find((c) => c.key === `${v.consent_record_type}:${v.consent_id}`)
  const consentStale = chosenConsent?.stale && !v.consent_reconfirmed

  const submit = (signNow) => {
    if (amending && !reason.trim()) { toast.error('An amendment needs a reason.'); return }
    setSaving(true)
    const payload = { clinical_note: v, sign_now: signNow, encounter_at: encounterAt || undefined }
    const done = { preserveScroll: true, onFinish: () => setSaving(false),
      onSuccess: () => { setV(blank()); setAmending(null); setReason(''); setEncounterAt('') } }

    if (amending) {
      router.post(`/patients/${patient.id}/clinical-notes/${amending.id}/amend`, { ...payload, reason }, done)
    } else {
      router.post(`/patients/${patient.id}/clinical-notes`, payload, done)
    }
  }

  const startAmend = (note) => {
    setAmending(note)
    setV({ ...blank(), ...FIELDS.reduce((a, f) => ({ ...a, [f.key]: note[f.key] || '' }), {}),
           do_nothing_discussed: !!note.do_nothing_discussed })
    setReason('')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const signNote = (note) => {
    if (!window.confirm('Sign and lock this note?\n\nOnce signed it cannot be edited — a correction has to be recorded as an amendment.')) return
    router.post(`/patients/${patient.id}/clinical-notes/${note.id}/sign`, {}, { preserveScroll: true })
  }

  return (
    <DashboardLayout>
      <Link href={`/patients/${patient.id}`} className="mb-4 inline-flex items-center gap-1 text-sm text-brand-muted hover:text-brand-ink">
        <ArrowLeft size={14} /> {patient.name}
      </Link>

      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-primary">
          <Stethoscope size={18} className="text-white" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-brand-ink">Clinical notes — {patient.name}</h1>
          <p className="text-sm text-brand-muted">What was found, what was discussed, what was agreed.</p>
        </div>
        <a href={`/patients/${patient.id}/record-export`} target="_blank" rel="noreferrer"
           className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-brand-border bg-white px-3 py-2 text-sm font-medium text-brand-ink hover:bg-brand-surface"
           title="Produce the complete record — every note, consent and version, in date order">
          <ShieldCheck size={14} /> Produce full record
        </a>
      </div>

      <div className="grid gap-5 lg:grid-cols-[1fr_300px]">
        <div className="space-y-4">
          <div className="rounded-xl border border-brand-border bg-white p-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-brand-ink">
                {amending ? `Amending note #${amending.id}` : 'New note'}
              </h2>
              {amending && (
                <button onClick={() => { setAmending(null); setV(blank()); setReason('') }}
                  className="text-xs font-semibold text-brand-muted underline">Cancel amendment</button>
              )}
            </div>

            {amending && (
              <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
                <label className="mb-1 block text-xs font-semibold text-blue-900">
                  Why is this being corrected? <span className="text-red-500">*</span>
                </label>
                <input value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. tooth number recorded incorrectly"
                  className="w-full rounded-lg border border-blue-200 p-2 text-sm" />
                <p className="mt-1 text-[11px] text-blue-800">
                  The original note stays on the record exactly as it was. This reason is printed beside it.
                </p>
              </div>
            )}

            {FIELDS.map((f) => (
              <div key={f.key} className="mb-3">
                <label className="mb-1 block text-sm font-medium text-brand-ink">{f.label}</label>
                <textarea rows={f.rows} value={v[f.key]} onChange={(e) => set(f.key, e.target.value)}
                  className="w-full rounded-lg border border-brand-border p-2.5 text-sm focus:border-brand-primary focus:outline-none" />
                <p className="mt-0.5 text-[11px] text-brand-muted">{f.hint}</p>
              </div>
            ))}

            <label className="mb-3 flex cursor-pointer items-start gap-2.5 rounded-lg border border-brand-border p-3">
              <input type="checkbox" checked={v.do_nothing_discussed}
                onChange={(e) => set('do_nothing_discussed', e.target.checked)} className="mt-0.5 h-4 w-4" />
              <span>
                <span className="block text-sm font-medium text-brand-ink">I discussed the option of doing nothing</span>
                <span className="block text-[11px] text-brand-muted">
                  Its absence is one of the most common findings against a practitioner.
                </span>
              </span>
            </label>

            {/* COMPREHENSION, not disclosure. Defence organisations are explicit that a signed
                form alone is not enough — the record has to show understanding was CHECKED.
                Everything above this point records what you SAID; this records what the patient
                took in. The quick phrases are one tap, so it is faster than reaching for paper. */}
            <div className={cn('mb-4 rounded-lg border p-3', v.comprehension_checked ? 'border-emerald-300 bg-emerald-50/50' : 'border-brand-border')}>
              <label className="flex cursor-pointer items-start gap-2.5">
                <input type="checkbox" checked={v.comprehension_checked}
                  onChange={(e) => set('comprehension_checked', e.target.checked)} className="mt-0.5 h-4 w-4" />
                <span>
                  <span className="block text-sm font-medium text-brand-ink">I checked that the patient understood (teach-back)</span>
                  <span className="block text-[11px] text-brand-muted">
                    {TEACH_BACK_PROMPT} A signed form on its own is not evidence of informed consent; this is.
                  </span>
                </span>
              </label>
              {v.comprehension_checked && (
                <div className="mt-2 pl-7">
                  <div className="mb-1.5 flex flex-wrap gap-1.5">
                    {QUICK_PHRASES.map((phrase) => (
                      <button key={phrase} type="button"
                        onClick={() => set('comprehension_note', v.comprehension_note ? v.comprehension_note + ' ' + phrase : phrase)}
                        className="rounded-full border border-brand-border bg-white px-2.5 py-1 text-[11px] font-medium text-brand-ink hover:border-brand-primary hover:bg-brand-surface">
                        + {phrase.replace(/\.$/, '')}
                      </button>
                    ))}
                  </div>
                  <textarea rows={2} value={v.comprehension_note} onChange={(e) => set('comprehension_note', e.target.value)}
                    placeholder="What the patient said back, in short."
                    className="w-full rounded-lg border border-brand-border p-2.5 text-sm focus:border-brand-primary focus:outline-none" />
                </div>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-brand-muted">Consent this note relies on</label>
                <select
                  value={v.consent_record_type ? `${v.consent_record_type}:${v.consent_id}` : ''}
                  onChange={(e) => {
                    // The value is "Type:id" — a bare id is ambiguous, because a Consent and a
                    // FormSubmission can share one.
                    const [type, id] = e.target.value.split(':')
                    setV((p) => ({ ...p, consent_record_type: type || '', consent_id: id || '' }))
                  }}
                  className="w-full rounded-lg border border-brand-border bg-white p-2 text-sm">
                  <option value="">— none linked —</option>
                  {consents.map((c) => (
                    <option key={c.key} value={c.key}>{c.label} · signed {c.signed_at}{c.stale ? ' (old)' : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-brand-muted">Course of treatment</label>
                <select value={v.course_of_treatment_id} onChange={(e) => set('course_of_treatment_id', e.target.value)}
                  className="w-full rounded-lg border border-brand-border bg-white p-2 text-sm">
                  <option value="">— none —</option>
                  {courses.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
            </div>

            {consentStale && (
              <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
                <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-900">
                  <AlertTriangle size={13} /> That consent is more than {stale_after_days} days old.
                </p>
                <p className="mt-1 text-[11px] text-amber-800">
                  Consent has to be reviewed close to the time of treatment (HPCSA Booklet 4 §15.1).
                  Confirm with the patient that it still stands before you treat.
                </p>
                <label className="mt-2 flex cursor-pointer items-center gap-2 text-xs font-medium text-amber-900">
                  <input type="checkbox" checked={v.consent_reconfirmed}
                    onChange={(e) => set('consent_reconfirmed', e.target.checked)} className="h-4 w-4" />
                  I re-confirmed it with the patient today
                </label>
              </div>
            )}

            {/* NHA s6(2) / Booklet 4 — the language the consultation was actually held in, and
                who interpreted. Both columns existed and nothing ever set them, so every note
                silently asserted English. */}
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-brand-muted">Language of this consultation</label>
                <select value={v.language} onChange={(e) => set('language', e.target.value)}
                  className="w-full rounded-lg border border-brand-border bg-white p-2 text-sm">
                  <option value="en">English</option>
                  <option value="af">Afrikaans</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-brand-muted">
                  Interpreter <span className="font-normal">(if one was used)</span>
                </label>
                <input type="text" value={v.interpreter_name} onChange={(e) => set('interpreter_name', e.target.value)}
                  placeholder="Name and relationship"
                  className="w-full rounded-lg border border-brand-border p-2 text-sm" />
              </div>
            </div>

            <div className="mt-4 border-t border-brand-border pt-4">
              <label className="mb-1 block text-xs font-medium text-brand-muted">
                When did this encounter happen? <span className="font-normal">(leave blank for now)</span>
              </label>
              <input type="datetime-local" value={encounterAt} onChange={(e) => setEncounterAt(e.target.value)}
                className="rounded-lg border border-brand-border p-2 text-sm" />
              <p className="mt-0.5 text-[11px] text-brand-muted">
                Setting this more than 24 hours back marks the note as a late entry, which is honest and expected —
                a system that hides it is the problem.
              </p>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <button onClick={() => submit(false)} disabled={saving}
                className="rounded-lg border border-brand-border px-4 py-2 text-sm font-semibold text-brand-ink hover:bg-brand-surface disabled:opacity-50">
                {saving ? 'Saving…' : 'Save as draft'}
              </button>
              <button onClick={() => submit(true)} disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-primary px-4 py-2 text-sm font-semibold text-white hover:brightness-95 disabled:opacity-50">
                <Lock size={14} /> {saving ? 'Saving…' : 'Save & sign'}
              </button>
            </div>
          </div>

          <div>
            <h2 className="mb-2 text-sm font-semibold text-brand-ink">
              On the record ({notes.length})
            </h2>
            <div className="space-y-3">
              {notes.length === 0 && (
                <p className="rounded-xl border border-brand-border bg-white p-4 text-sm text-brand-muted">
                  No clinical notes yet for this patient.
                </p>
              )}
              {notes.map((n) => <NoteCard key={n.id} note={n} onSign={signNote} onAmend={startAmend} />)}
            </div>
          </div>
        </div>

        <div className="lg:sticky lg:top-4 lg:self-start">
          <Meter score={score} missing={missing} weights={weights} />
        </div>
      </div>
    </DashboardLayout>
  )
}
